<template>
    <HeaderDiv>
        <div class="header">
            <div class="menu-container" @mouseover="menuOpen()">
                <div class="menu">
                    <div class="main-menu">
                        <router-link to="/order">
                            <img src="/logo.png" alt="로고" style="height: 40px;" />
                        </router-link>
                    </div>
                </div>
                <div class="menu">
                    <div class="main-menu" :class="{ active: isCurrentRoute('/claim') }">
                        <router-link to="/claim" @click="menuClose()">청구 조회</router-link>
                    </div>
                </div>
                <div class="menu">
                    <div class="main-menu" :class="{ active: isCurrentRoute('/order') }">
                        <router-link to="/order" @click="menuClose()">주문 관리</router-link>
                    </div>
                </div>
                <div class="menu">
                    <div class="main-menu" :class="{ active: isCurrentRoute('/productlist') }">
                        <router-link to="/productlist" @click="menuClose()">재고 관리</router-link>
                    </div>
                </div>
                <div class="menu">
                    <div class="main-menu" :class="{ active: isCurrentRoute('/rental') }">
                        <router-link to="/rental" @click="menuClose()">대여 관리</router-link>
                    </div>
                </div>
                <div class="menu">
                    <div class="main-menu" :class="{ active: isCurrentRoute('/payment') }">
                        <router-link to="/payment" @click="menuClose()">결제 관리</router-link>
                    </div>
                </div>
                <div class="menu" v-if="$globalState.company_key === 32">
                    <div class=" main-menu" :class="{ active: isCurrentRoute('/account') }">
                        <router-link to="/account" @click="menuClose()">가상계좌 관리</router-link>
                    </div>
                </div>
                <div class="menu">
                    <div class="main-menu" :class="{ active: isCurrentRoute('/manage/company') }">
                        <router-link to="/manage/company" @click="menuClose()">의료기 관리</router-link>
                    </div>
                </div>
            </div>
            <div class="account">
                <router-link v-if="!isLoggedIn" to="/login">로그인</router-link>
                <template v-if="isLoggedIn">
                    <!-- 유저 아이콘과 메시지 알림 -->
                    <UserSection class="user-section" @click="toggleMessageDropdown">
                        <UserIconContainer>
                            <MessageBadge :class="{ 'has-messages': unreadMessageCount > 0 }">
                                {{ unreadMessageCount > 99 ? '99+' : unreadMessageCount }}
                            </MessageBadge>
                        </UserIconContainer>
                        <UserId class="user-id">{{ userId }}</UserId>
                    </UserSection>

                    <!-- 메시지 드롭다운 -->
                    <MessageDropdown class="message-dropdown" v-if="isMessageDropdownOpen" @click.stop>
                        <MessageHeader>
                            <h3>알림 메시지</h3>
                            <MarkAllReadButton @click="markAllAsRead" v-if="unreadMessageCount > 0">
                                모두 읽음
                            </MarkAllReadButton>
                        </MessageHeader>
                        <MessageList>
                            <NoMessages v-if="messages.length === 0">
                                <p>새로운 알림이 없습니다</p>
                            </NoMessages>
                            <div v-else>
                                <MessageItem v-for="message in messages" :key="message.id"
                                    :class="{ 'unread': !message.isRead }" @click="toggleMessageRead(message.id)">
                                    <MessageContent>
                                        <MessageTitle>{{ message.title }}</MessageTitle>
                                        <MessageText style="white-space: pre-line;">{{ message.text }}</MessageText>
                                        <MessageText v-if="message.details"
                                            style="white-space: pre-line; font-size: 0.9em; color: #666; margin-top: 4px;">
                                            {{ message.details }}</MessageText>
                                        <MessageTime>{{ formatTime(message.timestamp) }}</MessageTime>
                                    </MessageContent>
                                    <MessageActions>
                                        <DeleteMessageButton @click.stop="deleteMessage(message.id)" title="메시지 삭제">
                                            <Close :size="15" />
                                        </DeleteMessageButton>
                                    </MessageActions>
                                </MessageItem>
                            </div>
                        </MessageList>
                        <MessageFooter>
                            <ClearAllButton @click="clearAllMessages" v-if="messages.length > 0">
                                모든 메시지 삭제
                            </ClearAllButton>
                        </MessageFooter>
                    </MessageDropdown>

                    <span class="logout" @click="handleLogout">로그아웃</span>
                </template>
            </div>
        </div>
    </HeaderDiv>
</template>

<script>
import * as HeaderCss from '@/assets/styles/global/HeaderCss.js';

import Close from "vue-material-design-icons/Close.vue";

export default {
    name: 'HeaderComponent',

    components: {
        ...HeaderCss,
        Close
    },

    inject: ['globalMessageSystem'],

    data() {
        return {
            isMenuOpen: false,
            isLoggedIn: false,
            userId: '',
            isAdmin: false,
            authCheckInProgress: false,
            isMessageDropdownOpen: false,
            messages: [],
            unsubscribe: null
        }
    },

    computed: {
        unreadMessageCount() {
            return this.messages.filter(msg => !msg.isRead).length;
        }
    },

    created() {
        this.checkLoginStatus();
        this.loadMessages();
        this.setupGlobalMessageListener();
    },

    mounted() {
        this.$router.afterEach(() => {
            this.checkLoginStatus();
        });

        // 외부 클릭 시 드롭다운 닫기
        document.addEventListener('click', this.handleOutsideClick);
    },

    beforeUnmount() {
        document.removeEventListener('click', this.handleOutsideClick);
        if (this.unsubscribe) {
            this.unsubscribe();
        }
    },

    methods: {
        // 전역 메시지 리스너 설정
        setupGlobalMessageListener() {
            if (this.globalMessageSystem) {

                // 기존 구독 해제
                if (this.unsubscribe) {
                    this.unsubscribe();
                }

                // 새로운 구독 설정
                this.unsubscribe = this.globalMessageSystem.subscribe((messages) => {
                    // Vue의 반응성을 위해 새로운 배열로 할당
                    this.messages = [...messages];
                });

            } else {
                console.error('[Header] globalMessageSystem이 없습니다');
            }
        },

        // 메시지 드롭다운 토글
        toggleMessageDropdown() {
            this.isMessageDropdownOpen = !this.isMessageDropdownOpen;
            // 드롭다운이 열릴 때 최신 메시지 로드
            if (this.isMessageDropdownOpen) {
                this.loadMessages();
            }
        },

        // 외부 클릭 처리
        handleOutsideClick(event) {
            // UserSection과 MessageDropdown을 제외한 모든 클릭에서 드롭다운 닫기
            if (!event.target.closest('.user-section') && !event.target.closest('.message-dropdown')) {
                this.isMessageDropdownOpen = false;
            }
        },

        // 메시지 읽음/읽지 않음 토글 처리
        toggleMessageRead(messageId) {
            const message = this.messages.find(msg => msg.id === messageId);
            if (message) {
                message.isRead = !message.isRead;
                // 전역 메시지 시스템에도 반영
                if (this.globalMessageSystem) {
                    this.globalMessageSystem.markAsRead(messageId);
                }
            }
        },

        // 모든 메시지 읽음 처리
        markAllAsRead() {
            this.messages.forEach(message => {
                message.isRead = true;
            });
            // 전역 메시지 시스템에도 반영
            if (this.globalMessageSystem) {
                this.globalMessageSystem.markAllAsRead();
            }
        },

        // 메시지 삭제
        deleteMessage(messageId) {
            if (this.globalMessageSystem) {
                this.globalMessageSystem.deleteMessage(messageId);
            }
        },

        // 모든 메시지 삭제
        clearAllMessages() {
            if (this.globalMessageSystem) {
                this.globalMessageSystem.clearAllMessages();
            }
        },

        // 시간 포맷팅
        formatTime(timestamp) {
            const now = new Date();
            const diff = now - timestamp;
            const minutes = Math.floor(diff / 60000);
            const hours = Math.floor(diff / 3600000);
            const days = Math.floor(diff / 86400000);

            if (minutes < 1) return '방금 전';
            if (minutes < 60) return `${minutes}분 전`;
            if (hours < 24) return `${hours}시간 전`;
            if (days < 7) return `${days}일 전`;

            return timestamp.toLocaleDateString('ko-KR');
        },

        // 메시지 로드
        loadMessages() {
            if (this.globalMessageSystem) {
                this.messages = [...this.globalMessageSystem.getMessages()];
            }
        },

        handleLogout() {
            this.$axios.post(`${this.$apiUrls.hanqmedi}/logout`)
                .then(res => {
                    console.log("Logged out successfully", res.data)

                    // 로그아웃 전에 현재 사용자의 메시지 삭제
                    if (this.globalMessageSystem) {
                        this.globalMessageSystem.clearUserMessages();
                    }

                    // 모든 전역 상태 초기화
                    this.$setGlobalState('id', '');
                    this.$setGlobalState('manager_name', '');
                    this.$setGlobalState('company_name', '');
                    this.$setGlobalState('company_key', '');
                    this.$setGlobalState('barobill_id', '');
                    this.$setGlobalState('barobill_api_key', '');
                    this.$setGlobalState('popbill_id', '');
                    this.$setGlobalState('popbill_api_key', '');
                    this.$setGlobalState('thebill_id', '');
                    this.$setGlobalState('thebill_api_key', '');
                    this.$setGlobalState('company_address', '');
                    this.$setGlobalState('company_bnum', '');
                    this.$setGlobalState('company_ceo_name', '');
                    this.$setGlobalState('company_certificate_id', '');
                    this.$setGlobalState('company_certificate_pw', '');
                    this.$setGlobalState('company_cnum', '');
                    this.$setGlobalState('company_email', '');
                    this.$setGlobalState('company_fax', '');
                    this.$setGlobalState('company_industry', '');
                    this.$setGlobalState('company_phone', '');
                    this.$setGlobalState('company_share_id', '');
                    this.$setGlobalState('company_status', '');
                    this.$setGlobalState('company_type', '');
                    this.$setGlobalState('user_email', '');
                    this.$setGlobalState('user_phone', '');
                    this.$setGlobalState('department', '');
                    this.$setGlobalState('role', '');
                    this.$setGlobalState('isAdmin', false);
                    this.$setGlobalState('bank_info', '');
                    this.$setGlobalState('bank_name', '');
                    this.$setGlobalState('bank_number', '');
                    this.$setGlobalState('bank_username', '');

                    // 모든 스토리지 완전 삭제
                    this.clearAllStorage();

                    // 로그인 상태 초기화
                    this.isLoggedIn = false;
                    this.userId = '';

                    // 로그인 페이지로 이동
                    this.$router.push('/login');

                    // 성공 메시지 표시
                    this.$swal.fire({
                        icon: 'success',
                        title: '로그아웃 성공',
                        text: '로그아웃 되었습니다.',
                        confirmButtonText: '확인'
                    });
                })
                .catch(err => {
                    console.error('Logout failed:', err);

                    // 에러가 발생해도 메시지 삭제
                    if (this.globalMessageSystem) {
                        this.globalMessageSystem.clearUserMessages();
                    }

                    // 에러가 발생해도 클라이언트 측 데이터는 정리
                    this.clearAllStorage();
                    this.isLoggedIn = false;
                    this.userId = '';

                    this.$swal.fire({
                        icon: 'error',
                        title: '로그아웃 실패',
                        text: '로그아웃에 실패했습니다. 클라이언트 측 데이터는 정리되었습니다.',
                        confirmButtonText: '확인'
                    }).then(() => {
                        // 에러가 발생해도 로그인 페이지로 이동
                        this.$router.push('/login');
                    });
                });
        },

        // 모든 스토리지를 완전히 삭제하는 메서드
        clearAllStorage() {
            try {
                // 특정 키들을 명시적으로 삭제
                const specificKeys = [
                    'orderPageVisited',
                    'token',
                    'userInfo',
                    'orderPageData',
                    'orderFilters',
                    'orderSearchHistory',
                    'orderSortSettings',
                    'orderPageSize',
                    'orderCurrentPage'
                ];

                // 로컬 스토리지에서 특정 키들 삭제
                specificKeys.forEach(key => {
                    if (localStorage.getItem(key)) {
                        localStorage.removeItem(key);
                    }
                });

                // 사용자별 메시지 키 패턴으로 삭제 (현재 사용자의 메시지만)
                if (this.userId) {
                    const userMessageKey = `globalMessages_${this.userId}`;
                    localStorage.removeItem(userMessageKey);
                }

                // 세션 스토리지에서 특정 키들 삭제
                specificKeys.forEach(key => {
                    if (sessionStorage.getItem(key)) {
                        sessionStorage.removeItem(key);
                    }
                });

                // 로컬 스토리지 완전 삭제
                localStorage.clear();

                // 세션 스토리지 완전 삭제
                sessionStorage.clear();

                // 추가 검증: 실제로 삭제되었는지 확인
                this.verifyStorageCleared();
            } catch (error) {
                console.error('스토리지 삭제 중 오류 발생:', error);
            }
        },

        // 스토리지가 실제로 삭제되었는지 검증
        verifyStorageCleared() {
            try {
                const localStorageKeys = Object.keys(localStorage);
                const sessionStorageKeys = Object.keys(sessionStorage);

                if (localStorageKeys.length > 0) {
                    console.warn('로컬 스토리지에 남아있는 키들:', localStorageKeys);
                    // 남아있는 키들을 강제로 삭제
                    localStorageKeys.forEach(key => {
                        localStorage.removeItem(key);
                    });
                }

                if (sessionStorageKeys.length > 0) {
                    console.warn('세션 스토리지에 남아있는 키들:', sessionStorageKeys);
                    // 남아있는 키들을 강제로 삭제
                    sessionStorageKeys.forEach(key => {
                        sessionStorage.removeItem(key);
                    });
                }
            } catch (error) {
                console.error('스토리지 검증 중 오류 발생:', error);
            }
        },

        menuOpen() {
            this.isMenuOpen = true
        },

        menuClose() {
            this.isMenuOpen = false
        },

        // 전역 상태 복원 함수
        restoreGlobalState(userInfo) {
            // 기본 사용자 정보
            const userId = userInfo.user_name || userInfo.name || userInfo.id || '';
            this.$setGlobalState('id', userId);
            this.$setGlobalState('manager_name', userInfo.name || '');
            this.$setGlobalState('company_name', userInfo.company || userInfo.company_name || '');
            this.$setGlobalState('company_key', userInfo.company_id || userInfo.company_key || '');

            // 전역 메시지 시스템에 사용자 ID 설정
            if (this.globalMessageSystem && userId) {
                this.globalMessageSystem.setUserId(userId);
            }

            // 바로빌 관련 정보
            this.$setGlobalState('barobill_id', userInfo.barobill_id || '');
            this.$setGlobalState('barobill_api_key', userInfo.barobill_api_key || '');

            // 팝빌 관련 정보
            this.$setGlobalState('popbill_id', userInfo.popbill_id || '');
            this.$setGlobalState('popbill_api_key', userInfo.popbill_api_key || '');

            // 더빌 관련 정보
            this.$setGlobalState('thebill_id', userInfo.thebill_id || '');
            this.$setGlobalState('thebill_api_key', userInfo.thebill_api_key || '');

            // 회사 상세 정보
            this.$setGlobalState('company_address', userInfo.company_address || '');
            this.$setGlobalState('company_bnum', userInfo.company_bnum || '');
            this.$setGlobalState('company_ceo_name', userInfo.company_ceo_name || '');
            this.$setGlobalState('company_certificate_id', userInfo.company_certificate_id || '');
            this.$setGlobalState('company_certificate_pw', userInfo.company_certificate_pw || '');
            this.$setGlobalState('company_cnum', userInfo.company_cnum || '');
            this.$setGlobalState('company_email', userInfo.company_email || '');
            this.$setGlobalState('company_fax', userInfo.company_fax || '');
            this.$setGlobalState('company_industry', userInfo.company_industry || '');
            this.$setGlobalState('company_phone', userInfo.company_phone || '');
            this.$setGlobalState('company_share_id', userInfo.company_share_id || '');
            this.$setGlobalState('company_status', userInfo.company_status || '');
            this.$setGlobalState('company_type', userInfo.company_type || '');

            // 사용자 상세 정보
            this.$setGlobalState('user_email', userInfo.user_email || '');
            this.$setGlobalState('user_phone', userInfo.user_phone || '');
            this.$setGlobalState('department', userInfo.department || '');
            this.$setGlobalState('role', userInfo.role || '');
            this.$setGlobalState('isAdmin', userInfo.isAdmin || false);

            // 기타 정보
            this.$setGlobalState('bank_info', userInfo.bank_info || '');
            this.$setGlobalState('bank_name', userInfo.bank_name || '');
            this.$setGlobalState('bank_number', userInfo.bank_number || '');
            this.$setGlobalState('bank_username', userInfo.bank_username || '');
        },

        checkLoginStatus() {
            // 이미 인증 확인이 진행 중이면 중복 실행 방지
            if (this.authCheckInProgress) {
                return;
            }

            const token = localStorage.getItem('token');
            const userInfoStr = localStorage.getItem('userInfo');

            if (token && userInfoStr) {
                try {
                    // localStorage에서 userInfo 가져오기
                    const userInfo = JSON.parse(userInfoStr);

                    // 전역 상태 복원 (중요! - 이 안에서 사용자 ID 설정됨)
                    this.restoreGlobalState(userInfo);

                    // UI 업데이트
                    this.userId = userInfo.id;
                    this.isAdmin = userInfo.isAdmin || false;
                    this.isLoggedIn = true;

                    // 서버 인증 확인 (백그라운드에서 실행)
                    this.authCheckInProgress = true;

                    this.$axios.get(`${this.$apiUrls.hanqmedi}/auth`, {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        },
                        timeout: 5000
                    })
                        .then(res => {

                            if (res.data.isLoggedIn) {
                                // 서버에서 받은 최신 정보로 localStorage 업데이트
                                const updatedUserInfo = {
                                    ...userInfo,
                                    ...res.data.userInfo
                                };
                                localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));

                                // 전역 상태 다시 복원
                                this.restoreGlobalState(updatedUserInfo);

                                // UI 업데이트
                                this.userId = updatedUserInfo.id;
                                this.isAdmin = updatedUserInfo.isAdmin || false;
                                this.isLoggedIn = true;

                            } else {
                                this.handleAuthFailure();
                            }
                        })
                        .catch((error) => {
                            console.error('서버 인증 오류:', error);

                            // 네트워크 오류나 타임아웃의 경우 localStorage 정보를 유지
                            if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
                                // localStorage 정보를 그대로 사용 (전역 상태는 이미 복원됨)
                            } else if (error.response && error.response.status === 401) {
                                this.handleAuthFailure();
                            } else {
                                console.log('기타 오류 - localStorage 정보 유지');
                                // 기타 오류의 경우 localStorage 정보를 유지 (전역 상태는 이미 복원됨)
                            }
                        })
                        .finally(() => {
                            this.authCheckInProgress = false;
                        });

                } catch (error) {
                    console.error('userInfo 파싱 오류:', error);
                    this.resetUserState();
                    this.authCheckInProgress = false;
                }
            } else {
                this.resetUserState();
            }
        },

        handleAuthFailure() {
            // 메시지 삭제
            if (this.globalMessageSystem) {
                this.globalMessageSystem.clearUserMessages();
            }

            // 전역 상태 초기화
            this.$setGlobalState('id', '');
            this.$setGlobalState('manager_name', '');
            this.$setGlobalState('company_name', '');
            this.$setGlobalState('company_key', '');

            localStorage.removeItem('token');
            localStorage.removeItem('userInfo');

            this.resetUserState();

            // 로그인 페이지로 리다이렉트
            if (this.$route.path !== '/login') {
                this.$router.push('/login');
            }
        },

        resetUserState() {
            this.isLoggedIn = false;
            this.userId = '';
            this.isAdmin = false;

            // 메시지 시스템 초기화
            if (this.globalMessageSystem) {
                this.globalMessageSystem.setUserId(null);
            }
        },

        isCurrentRoute(path) {
            return this.$route.path === path;
        }
    },

    watch: {
        '$route'() {
            this.checkLoginStatus();
            this.$forceUpdate();
        }
    }
}
</script>